/**
 * VerdictLandingPage
 *
 * Landing page for the VERDICT rebrand.
 * Design Philosophy: "Decision Engine" not "Developer Tool"
 *
 * Key differentiators from Factory.ai:
 * - Glass search bar (not terminal CLI)
 * - Powered by Council (not fake Trusted By logos)
 * - Radial convergence visualization (not linear pipeline)
 * - Ambient gold spotlight (truth emerging from darkness)
 * - Editorial typography with serif accents
 *
 * Research Flow:
 * - When a user submits a query, the right side transforms to show
 *   real-time research progress with task breakdown and agent activity
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'framer-motion';
import CountUp from 'react-countup';
import { SEO } from '@/components/SEO';
import { ArrowRight, ArrowLeft, Search, Menu, X, User, Terminal, Settings, LogOut, BookOpen, ExternalLink, Upload, AlertTriangle, Layers, TrendingUp, ChevronDown, Shield, Sparkles, Sun, Moon } from 'lucide-react';
import { UserMenu } from '@/components/layout/UserMenu';
import {
  SectionLabel,
  ResearchInput,
  CouncilVisualizationWithDemo,
  CouncilBadge,
  ModelCarousel,
  VerdictFooter,
  ScrambleText,
  LogicTree,
  SealedVerdictOverlay,
  BlueprintExamples,
  PortfolioExample,
} from '@/components/verdict';
import { FinancialLogicTree } from '@/components/verdict/FinancialLogicTree';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useResearchSession } from '@/hooks/useResearchSession';
import { useFinancialStream } from '@/hooks/useFinancialStream';
import { useFingerprint } from '@/hooks/useFingerprint';
import { useCredits } from '@/hooks/useCredits';
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { ResearchIntensity, ResearchMode } from '@/types';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';

/* ---------- Benchmark chart data (from actual evaluations: benchmarkData.ts) ---------- */
const BENCH_CLR = { frontier: '#6b7280', council: '#D4A574' };

/*
 * Radar: Financial Analysis dimension scores (only mode to win all 6 dimensions).
 * Scores are out of 5, scaled to 0-100 for the radar chart.
 */
const BENCH_RADAR = [
  { metric: 'Calibration', frontier: 78.8, council: 87.2 },
  { metric: 'Risk Aware.', frontier: 89.6, council: 93.2 },
  { metric: 'Accuracy', frontier: 80.4, council: 83.4 },
  { metric: 'Actionability', frontier: 86.8, council: 89.6 },
  { metric: 'Clarity', frontier: 87.0, council: 87.8 },
  { metric: 'Depth', frontier: 86.2, council: 85.8 },
];

/* Win rates by mode — actual head-to-head product comparison results */
const BENCH_BARS = [
  { title: 'Strategic Blueprint', hint: 'Head-to-head win rate', data: [{ name: 'Frontier', v: 25 }, { name: 'Vettis', v: 75 }], max: 100, sfx: '%' },
  { title: 'Financial Analysis', hint: 'Head-to-head win rate', data: [{ name: 'Frontier', v: 33 }, { name: 'Vettis', v: 67 }], max: 100, sfx: '%' },
  { title: 'Actionability Advantage', hint: 'Strategic mode, Δ score', data: [{ name: 'Frontier', v: 4.21 }, { name: 'Vettis', v: 4.76 }], max: 5, sfx: '' },
  { title: 'Calibration Advantage', hint: 'Financial mode, Δ score', data: [{ name: 'Frontier', v: 3.94 }, { name: 'Vettis', v: 4.36 }], max: 5, sfx: '' },
];

const BENCH_BAR_FILLS = [`${BENCH_CLR.frontier}99`, BENCH_CLR.council];

/* Custom Y-axis tick that highlights the "Vettis" label */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function BenchYTick(props: any) {
  const { x, y, payload } = props;
  const isVettis = payload?.value === 'Vettis';
  return (
    <text
      x={x}
      y={y}
      textAnchor="end"
      dominantBaseline="central"
      fill={isVettis ? '#D4A574' : 'rgb(var(--color-text-secondary))'}
      fontSize={isVettis ? 11 : 10}
      fontWeight={isVettis ? 700 : 400}
    >
      {payload?.value}
    </text>
  );
}

function BenchBarTip({ active, payload, sfx }: { active?: boolean; payload?: Array<{ value: number; payload: { name: string } }>; sfx: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface/95 border border-border-subtle rounded-lg px-3 py-2 text-xs shadow-xl backdrop-blur-sm">
      <span className="text-text-secondary">{payload[0].payload.name}: </span>
      <span className="text-text-primary font-semibold">{payload[0].value}{sfx}</span>
    </div>
  );
}

/* ---------- Full-answer rich-text renderer ---------- */
function FormattedAnswer({ text, isVettis }: { text: string; isVettis?: boolean }) {
  const paragraphs = text.split('\n\n');
  const accent = isVettis ? 'text-verdict-primary' : 'text-text-primary';
  const accentMuted = isVettis ? 'text-verdict-primary/70' : 'text-text-secondary';
  const accentBorder = isVettis ? 'border-verdict-primary/20' : 'border-border';

  /* Detect if a line is a metadata row (pipe-separated key-value pairs) */
  const isMetaLine = (l: string) => l.includes(' | ') && l.split(' | ').length >= 2;
  /* Detect ALL-CAPS title lines */
  const isAllCaps = (l: string) => l.length > 4 && l === l.toUpperCase() && /[A-Z]/.test(l);
  /* Detect numbered heading like "1. Weight loss & body composition" (short, no trailing period) */
  const isNumberedHeading = (l: string) => /^[0-9]+\.\s/.test(l) && l.length < 90 && !l.endsWith('.');
  /* Detect sub-heading: ends with ":" or ")" and is short */
  const isSubHeading = (l: string) => l.length < 100 && !l.startsWith('- ') && (l.endsWith(':') || (l.length < 70 && !l.endsWith('.') && !l.endsWith(',') && !/^[0-9]+\.\s/.test(l)));

  /* Inline formatting: bold text between ** or ALLCAPS labels like BULL: BEAR: CORRECTED */
  const formatInline = (str: string): React.ReactNode => {
    const parts = str.split(/(\b(?:BULL|BEAR|RISK FLAG|CORRECTED|NOTE|WARNING|IMPORTANT)\b:?)/g);
    if (parts.length <= 1) return str;
    return parts.map((part, i) =>
      /^(?:BULL|BEAR|RISK FLAG|CORRECTED|NOTE|WARNING|IMPORTANT):?$/.test(part)
        ? <span key={i} className={cn('font-bold', accent)}>{part}</span>
        : part
    );
  };

  return (
    <div className="space-y-4">
      {paragraphs.map((para, pIdx) => {
        const lines = para.split('\n');

        /* --- Title block: first paragraph, single short line or ALLCAPS --- */
        if (pIdx === 0 && lines.length <= 2 && (isAllCaps(lines[0]) || lines[0].length < 80)) {
          return (
            <div key={pIdx} className={cn('pb-3 mb-1 border-b', accentBorder)}>
              <h2 className={cn('text-lg sm:text-xl font-bold tracking-tight', accent)}>
                {lines[0]}
              </h2>
              {lines[1] && isMetaLine(lines[1]) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {lines[1].split(' | ').map((seg, i) => (
                    <span key={i} className={cn(
                      'text-[11px] sm:text-xs px-2 py-0.5 rounded-full border',
                      isVettis
                        ? 'bg-verdict-primary/10 border-verdict-primary/20 text-verdict-primary/80'
                        : 'bg-surface-light border-border text-text-secondary'
                    )}>
                      {seg.trim()}
                    </span>
                  ))}
                </div>
              )}
              {lines[1] && !isMetaLine(lines[1]) && (
                <p className={cn('text-xs sm:text-sm mt-1', accentMuted)}>{lines[1]}</p>
              )}
            </div>
          );
        }

        /* --- Metadata paragraph (pipe-separated) --- */
        if (lines.length === 1 && isMetaLine(lines[0])) {
          return (
            <div key={pIdx} className="flex flex-wrap gap-2">
              {lines[0].split(' | ').map((seg, i) => (
                <span key={i} className={cn(
                  'text-[11px] sm:text-xs px-2 py-0.5 rounded-full border',
                  isVettis
                    ? 'bg-verdict-primary/10 border-verdict-primary/20 text-verdict-primary/80'
                    : 'bg-surface-light border-border text-text-secondary'
                )}>
                  {seg.trim()}
                </span>
              ))}
            </div>
          );
        }

        /* --- Pure bullet list paragraph --- */
        const allBullets = lines.every((l) => l.startsWith('- ') || l.startsWith('  - '));
        if (allBullets) {
          return (
            <ul key={pIdx} className="space-y-1.5 pl-4">
              {lines.map((l, i) => (
                <li key={i} className="relative pl-3 text-[13px] sm:text-sm before:absolute before:left-0 before:top-[0.6em] before:w-1 before:h-1 before:rounded-full before:bg-current before:opacity-50">
                  {formatInline(l.replace(/^-\s+/, '').replace(/^\s+-\s+/, ''))}
                </li>
              ))}
            </ul>
          );
        }

        /* --- Pure numbered list paragraph --- */
        const allNumbered = lines.every((l) => /^[0-9]+\.\s/.test(l));
        if (allNumbered) {
          return (
            <ol key={pIdx} className="space-y-2 pl-5 list-decimal list-outside marker:text-text-muted marker:font-semibold marker:text-sm">
              {lines.map((l, i) => (
                <li key={i} className="pl-1 text-[13px] sm:text-sm">
                  {formatInline(l.replace(/^[0-9]+\.\s*/, ''))}
                </li>
              ))}
            </ol>
          );
        }

        /* --- Mixed content: render line by line --- */
        const rendered: React.ReactNode[] = [];
        let bulletBuf: string[] = [];
        let numberedBuf: string[] = [];

        const flushBullets = () => {
          if (!bulletBuf.length) return;
          rendered.push(
            <ul key={`b-${rendered.length}`} className="space-y-1 pl-4 mt-1">
              {bulletBuf.map((l, i) => (
                <li key={i} className="relative pl-3 text-[13px] sm:text-sm before:absolute before:left-0 before:top-[0.6em] before:w-1 before:h-1 before:rounded-full before:bg-current before:opacity-50">
                  {formatInline(l)}
                </li>
              ))}
            </ul>
          );
          bulletBuf = [];
        };
        const flushNumbered = () => {
          if (!numberedBuf.length) return;
          rendered.push(
            <ol key={`n-${rendered.length}`} className="space-y-1.5 pl-5 mt-1 list-decimal list-outside marker:text-text-muted marker:font-semibold marker:text-sm">
              {numberedBuf.map((l, i) => (
                <li key={i} className="pl-1 text-[13px] sm:text-sm">{formatInline(l)}</li>
              ))}
            </ol>
          );
          numberedBuf = [];
        };

        for (const line of lines) {
          if (line.startsWith('- ') || line.startsWith('  - ')) {
            flushNumbered();
            bulletBuf.push(line.replace(/^-\s+/, '').replace(/^\s+-\s+/, ''));
          } else if (isNumberedHeading(line)) {
            /* Numbered section heading like "1. Weight loss & body composition" */
            flushBullets(); flushNumbered();
            const label = line.replace(/^([0-9]+)\.\s*/, '');
            const num = line.match(/^([0-9]+)/)?.[1] ?? '';
            rendered.push(
              <div key={`nh-${rendered.length}`} className="flex items-baseline gap-2 mt-2">
                <span className={cn('text-xs font-bold tabular-nums opacity-50', accent)}>{num}.</span>
                <h4 className={cn('text-sm sm:text-base font-semibold', accent)}>{label}</h4>
              </div>
            );
          } else if (/^[0-9]+\.\s/.test(line)) {
            flushBullets();
            numberedBuf.push(line.replace(/^[0-9]+\.\s*/, ''));
          } else {
            flushBullets(); flushNumbered();
            if (isSubHeading(line)) {
              rendered.push(
                <h4 key={`sh-${rendered.length}`} className={cn('text-sm sm:text-[15px] font-semibold mt-2', accent)}>
                  {line}
                </h4>
              );
            } else {
              rendered.push(
                <p key={`p-${rendered.length}`} className="text-[13px] sm:text-sm leading-relaxed">
                  {formatInline(line)}
                </p>
              );
            }
          }
        }
        flushBullets(); flushNumbered();

        return <div key={pIdx} className="space-y-1">{rendered}</div>;
      })}
    </div>
  );
}

/* ---------- Landing FAQ data ---------- */
const LANDING_FAQ_ITEMS = [
  { q: 'What is Vettis?', a: 'Vettis is a high-stake decision engine that deploys multiple AI agents to research, debate, and verify answers before you see them. Instead of relying on a single AI model, Vettis assembles a council of competing agents — from OpenAI, Anthropic, Google, and more — that cross-check each other\'s work to deliver verified, actionable answers.' },
  { q: 'How is Vettis different from ChatGPT or other AI tools?', a: 'Single AI models like ChatGPT give you one perspective and can hallucinate without warning. Vettis runs multiple models in parallel, makes them debate their findings, flags contradictions, and synthesizes a verified consensus. Our benchmarks show this multi-agent approach consistently outperforms single-model responses on accuracy and risk awareness.' },
  { q: 'Is Vettis free to use?', a: 'Yes — you get 3 free analyses with no signup and no credit card. After that, paid plans start at $9/mo with monthly credit allocations. The Teams plan offers unlimited credits. All plans include access to every AI model and unlimited chat follow-ups.' },
  { q: 'What AI models does Vettis use?', a: 'Vettis uses GPT-4o, GPT-5.1, Claude, Gemini, DeepSeek, and other frontier models. Each research query assembles a council from multiple providers so no single vendor\'s biases dominate the result.' },
  { q: 'How accurate is Vettis compared to a single AI?', a: 'In blind evaluations scored by independent AI judges, Vettis wins 67-75% of head-to-head comparisons against frontier models on financial and strategic questions. It scores highest on actionability and calibration — giving you specific thresholds and honest confidence ranges instead of vague hedging.' },
];

function LandingFaqItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-border-subtle last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="text-sm sm:text-base font-medium text-text-primary group-hover:text-verdict-primary transition-colors pr-4">
          {question}
        </span>
        <ChevronDown className={cn('w-5 h-5 text-text-secondary transition-transform duration-300 flex-shrink-0', isOpen && 'rotate-180 text-verdict-primary')} />
      </button>
      <div className={cn('overflow-hidden transition-all duration-300', isOpen ? 'max-h-96 pb-5' : 'max-h-0')}>
        <p className="text-sm text-text-secondary leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

/* ---------- Portfolio feature data ---------- */
const PORTFOLIO_FEATURES = [
  {
    icon: Upload,
    title: 'Upload Holdings',
    description: 'Import your Fidelity CSV or PDF. Your entire portfolio, analyzed in minutes.',
  },
  {
    icon: TrendingUp,
    title: 'Bull vs Bear Stance',
    description: 'Every position gets an adversarial assessment. Know where the risks really are.',
  },
  {
    icon: AlertTriangle,
    title: 'Needs Attention Alerts',
    description: 'Bearish stance + losing money? Vettis flags positions that need your review.',
  },
  {
    icon: Layers,
    title: 'Deep Dive on Demand',
    description: 'One click to run a full adversarial deep dive on any position in your portfolio.',
  },
];

/* ---------- Answer Comparison data ---------- */
interface ComparisonAnswer {
  model: string;
  color: string;
  badge: string;
  badgeColor: string;
  excerpt: string;
  fullAnswer: string;
  issue: string[];
  isVettis?: boolean;
}
interface ComparisonExample {
  question: string;
  tag: string;
  answers: ComparisonAnswer[];
}

const ANSWER_COMPARISONS: ComparisonExample[] = [
  {
    question: 'Is Tesla a good investment after Q4 2024 earnings?',
    tag: 'Investment Analysis',
    answers: [
      {
        model: 'GPT 5.1',
        color: '#10a37f',
        badge: 'Speculative Framework',
        badgeColor: 'bg-red-500/15 text-red-400 border-red-500/20',
        excerpt: 'Tesla\'s Q4 2024 report was mixed and, in my view, the stock is high risk / speculative, not a clear "buy" for a conservative investor at current levels. I\'ll base this on the likely shape of Q4 given 2024 trends...',
        fullAnswer: 'Tesla\'s Q4 2024 report was mixed and, in my view, the stock is high risk / speculative, not a clear "buy" for a conservative investor at current levels.\n\nBecause I don\'t have live data, I\'ll base this on the likely shape of Q4 given 2024 trends and what matters most for valuation. You should verify the latest exact figures before acting, but the framework below will still apply.\n\n1. Core business trajectory\n\nKey things to look at in Q4:\n\n- Deliveries & growth: 2023 deliveries ~1.81M (+38% YoY). 2024 guidance was already softening; if Q4 confirms single-digit to low-teens % growth and no clear 2025 acceleration, Tesla starts to look like a mature auto OEM, not a hyper-growth tech company.\n\n- Margins: 2022 auto gross margin ex-credits ~30%. 2023 after price cuts: mid-teens. If Q4 2024 auto gross margin is still <20% and operating margin <10%, it is hard to justify a huge growth multiple unless FSD/energy are inflecting fast.\n\n2. Valuation versus fundamentals\n\nIf Tesla is around $200-$250/share:\n- Market cap: roughly $640-$800B\n- Assume 2024 EPS around $3-4 (you must confirm)\n- Implied P/E = 50-70x\n\nAuto peers trade at 6-12x, high-quality tech at 25-35x on solid double-digit growth.\n\n3. Key upside & downside drivers\n\nUpside (speculative): Breakthrough in FSD / robotaxi with regulatory progress. Successful launch of lower-cost "Gen 3" vehicle lifting volumes. Energy storage (Megapack) growing >50% YoY with strong margins.\n\nDownside: Price wars in EVs (China/US) further compress margins. Delays or under-delivery on FSD, robotaxi, or new models. Higher rates / risk-off market hurting long-duration growth stocks.\n\nRecommendation:\n- For long-term, risk-tolerant investors: Tesla can be a small speculative position (e.g., 2-5% of portfolio) if you believe in FSD + energy as large, profitable platforms by 2030.\n- For conservative / valuation-focused investors: wait for either clear proof of high-margin software/energy scaling, or a substantial valuation compression (e.g., P/E closer to 25-30x forward earnings).\n\nVerify Q4 numbers, especially: deliveries, auto gross margin ex-credits, operating margin, FSD/energy revenue, and 2025 guidance before committing.',
        issue: [
          'Admits it doesn\'t have actual Q4 data',
          'Speculates based on "likely shape" instead of real figures',
          'Gives a framework instead of analyzing the earnings the user asked about',
        ],
      },
      {
        model: 'Gemini 3',
        color: '#4285f4',
        badge: 'Contradicts Own Data',
        badgeColor: 'bg-red-500/15 text-red-400 border-red-500/20',
        excerpt: 'Tesla is a high-risk, high-reward investment. Revenue fell 9% YoY to $21.3B, EPS missed estimates at $0.45, free cash flow turned negative at -$2.53B. Recommendation: Speculative Buy / Hold...',
        fullAnswer: 'Note: Tesla has not yet reported Q4 2024 earnings (scheduled for January 2025). The following analysis is based on the most recent Q1 2024 earnings report released in April 2024 and current forward-looking guidance.\n\nRecommendation: Speculative Buy / Hold\n\nTesla is currently a high-risk, high-reward investment that requires investors to look past deteriorating fundamentals and bet on future execution in AI and robotics.\n\nThe Bear Case (Fundamentals):\nBased strictly on Q1 2024 numbers, Tesla is struggling.\n- Revenue Decline: Revenue fell 9% year-over-year to $21.3 billion, the steepest drop since 2012.\n- Missed Expectations: Adjusted EPS came in at $0.45, missing Wall Street estimates of $0.51.\n- Margin Compression: Gross margins dropped to 17.4% (down from 19.3% in Q1 2023) due to aggressive price cuts intended to stimulate waning demand.\n- Cash Flow: Free cash flow turned negative (-$2.53 billion) due to an inventory build-up and AI infrastructure spending.\n\nThe Bull Case (The Pivot):\nDespite poor financials, the stock rallied post-earnings because management successfully shifted the narrative from "slowing EV sales" to "AI and autonomy."\n- Affordable Models: Tesla accelerated the timeline for launching new, more affordable models to late 2024 or early 2025.\n- Robotaxi: CEO Elon Musk is betting the company\'s future on autonomy, with a "Cybercab" reveal scheduled for August 8, 2024.\n- FSD Licensing: Continued improvements in Full Self-Driving (FSD) v12 suggest potential high-margin software revenue streams.\n\nVerdict:\nIf you value Tesla as a traditional automaker, it is significantly overvalued with a P/E ratio over 45x amidst shrinking earnings. However, if you view Tesla as an AI and robotics company, the current dip represents a buying opportunity ahead of the Robotaxi cycle.',
        issue: [
          'Lays out devastating fundamentals (-9% revenue, negative FCF, margin compression)',
          'Still recommends "Speculative Buy" based entirely on narrative promises',
          'Completely ignores the energy storage segment',
        ],
      },
      {
        model: 'Claude Sonnet 4.5',
        color: '#d97706',
        badge: 'Overly Cautious',
        badgeColor: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
        excerpt: 'Based on Tesla\'s Q4 2024 earnings (reported January 2025), here\'s my assessment. Revenue: $25.7B, up only 2% YoY. Deliveries: 1.79M vehicles in 2024, below the 1.8M guidance. Recommendation: HOLD/CAUTIOUS...',
        fullAnswer: 'Tesla Q4 2024 Investment Analysis\n\nBased on Tesla\'s Q4 2024 earnings (reported January 2025), here\'s my assessment:\n\nKey Financial Metrics\n\nChallenges:\n- Revenue: $25.7B, up only 2% YoY (missed estimates of $27.2B)\n- Automotive revenue: Down 8% YoY to $19.8B\n- Deliveries: 1.79M vehicles in 2024, below the 1.8M guidance\n- Operating margin: Compressed to 8.5% from previous quarters\n- EPS: $0.73 (beat estimates of $0.76 on adjusted basis, but down 71% YoY)\n\nPositives:\n- Energy storage: Deployed 11.0 GWh in Q4, up 359% YoY\n- Free cash flow: $3.9B for the quarter\n- Cybertruck: Achieved profitability\n\nInvestment Recommendation: HOLD/CAUTIOUS\n\nReasons for Caution:\n1. Demand concerns: Vehicle delivery growth has stalled, raising questions about market saturation in current segments\n2. Margin pressure: Price cuts throughout 2024 compressed margins significantly\n3. Valuation: Trading at ~60-70x forward P/E despite slowing growth -- premium valuation requires growth acceleration\n4. Execution risk: The promised lower-cost vehicle and Full Self-Driving remain uncertain\n\nPotential Upside:\n- Energy business: Showing explosive growth and could become significant revenue driver\n- New models: Affordable vehicle platform expected in H1 2025\n- AI/Robotaxi: Long-term optionality, though timeline uncertain\n- Elon Musk factor: Track record of execution on ambitious goals\n\nBottom Line:\nTesla is not a clear buy at current levels for conservative investors. The core auto business shows weakness, and the valuation doesn\'t reflect the current growth rate. However, for risk-tolerant investors with 3-5 year horizons who believe in the energy/AI thesis, it could be a speculative hold.\n\nBetter entry point: Wait for either (1) sub-$180 price levels, (2) concrete evidence of demand recovery, or (3) successful launch of affordable vehicle platform.\n\nAlternative approach: Consider dollar-cost averaging small positions rather than large lump-sum investment given the current uncertainty.',
        issue: [
          'Has the right data but buries the energy storage breakthrough (359% YoY)',
          'Drowns the key signal under a wall of caution',
          'Ends with "HOLD" when the energy pivot is a thesis-changing signal',
        ],
      },
      {
        model: 'Vettis Council',
        color: '#D4A574',
        badge: 'Verified Consensus',
        badgeColor: 'bg-verdict-primary/15 text-verdict-primary border-verdict-primary/20',
        excerpt: 'After cross-referencing three independent analyses: Q4 revenue was $25.7B (+2% YoY). Automotive gross margin declined to 17.6%. Energy storage deployed 11.0 GWh (+359% YoY) -- a pivotal diversification signal. Consensus: cautiously bullish with margin compression as key risk.',
        fullAnswer: 'VETTIS COUNCIL CONSENSUS REPORT\nConfidence: High | Agents: 3/3 Agreed on Core Facts | Debate Rounds: 2\n\nAfter cross-referencing three independent analyses, the council reached the following verified consensus:\n\nVerified Financial Data (all agents agreed):\n- Q4 2024 revenue: $25.7B (+2% YoY)\n- Vehicle deliveries: 1.79M for full year 2024 (below 1.8M guidance)\n- Automotive gross margin: 17.6% (CORRECTED -- Agent-OpenAI could not provide actual Q4 figures and speculated; Agent-Anthropic provided the correct 8.5% operating margin from the 10-Q filing)\n- Energy storage: 11.0 GWh deployed in Q4, up 359% YoY -- this was initially underweighted by Agent-Anthropic and elevated during debate as a thesis-changing catalyst\n- Free cash flow: $3.9B for Q4\n\nKey Debate Resolutions:\n1. Agent-OpenAI admitted it lacked actual Q4 data and provided a speculative framework. The council flagged this and relied on Agent-Anthropic\'s concrete figures instead.\n2. Agent-Gemini presented devastating bear case data (-9% revenue, negative FCF) but still recommended "Speculative Buy" based on narrative promises alone. The council flagged this logical contradiction and also noted it completely ignored the energy storage segment.\n3. Agent-Anthropic buried the energy storage breakthrough under excessive caution. The council elevated this as the single most important signal in the report.\n\nInvestment Thesis (Consensus: Cautiously Bullish):\n- BULL: Energy storage is an underappreciated growth engine (+359% YoY GWh deployed). FSD licensing potential remains a free option. Cybertruck reached profitability.\n- BEAR: Automotive margin compression is real and accelerating. Competition from BYD is structural, not cyclical. Valuation at ~60-70x forward earnings requires flawless execution.\n- RISK FLAG: If automotive margins fall below 7% in 2025, the bull thesis weakens significantly.\n\nActionable Summary:\nTesla is transitioning from a pure-play EV company to a diversified energy/AI platform. The 359% energy storage surge is the most important signal in this report and was underweighted by two of three agents. Suitable for investors with 3+ year horizons who can tolerate volatility. Not suitable for income-focused or conservative portfolios.\n\nUncertainty Disclosure: FSD revenue timeline and Optimus commercialization remain speculative. The council cannot verify forward-looking claims about these programs.',
        issue: [
          'Caught that one agent speculated without data',
          'Flagged another for contradicting its own bear case with a buy recommendation',
          'Elevated the energy storage breakthrough the third agent buried',
          'Synthesized the correct picture from all three',
        ],
        isVettis: true,
      },
    ],
  },
  {
    question: 'What are the proven health benefits of intermittent fasting?',
    tag: 'Health Research',
    answers: [
      {
        model: 'GPT 5.1',
        color: '#10a37f',
        badge: 'Mixes Evidence Levels',
        badgeColor: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
        excerpt: 'Intermittent fasting (IF) has a growing evidence base, especially for metabolic health. Most human data come from time-restricted eating (TRE) and 5:2 or alternate-day fasting. Meta-analysis of 27 RCTs: IF produced 0.8-13% weight loss...',
        fullAnswer: 'Intermittent fasting (IF) has a growing evidence base, especially for metabolic health. Most human data come from time-restricted eating (TRE, e.g., 8-hour eating window) and 5:2 or alternate-day fasting.\n\n1. Weight loss & body composition\n- Meta-analysis of 27 RCTs (Harris et al., Nutrients 2018): IF produced 0.8-13% weight loss over 2-52 weeks, similar to continuous calorie restriction.\n- RCT (Trepanowski et al., JAMA Intern Med 2017): Alternate-day fasting and daily calorie restriction led to similar ~6-7% weight loss; ADF showed slightly greater fat-mass reduction.\n\n2. Insulin sensitivity & type 2 diabetes risk\n- Early time-restricted feeding (e.g., 8 a.m.-2 p.m.) in men with prediabetes improved insulin sensitivity, beta-cell responsiveness, blood pressure, and oxidative stress without weight loss (Sutton et al., Cell Metab 2018).\n- Meta-analysis of 11 RCTs in people with metabolic syndrome/T2D (Meng et al., J Clin Endocrinol Metab 2020): IF reduced fasting glucose (~4-7 mg/dL) and HOMA-IR, especially with early eating windows.\n\n3. Lipids & cardiovascular risk factors\n- Systematic review (Stekovic et al., Annu Rev Nutr 2020): IF typically reduces LDL by ~5-15%, triglycerides by ~10-20%, and modestly lowers blood pressure.\n- 5:2 IF in overweight adults led to greater reduction in insulin resistance and similar lipid improvements compared to daily restriction over 6 months (Harvie et al., Int J Obes 2013).\n\n4. Inflammation & oxidative stress\n- Early TRE study (Sutton 2018): Reduced markers of oxidative stress and evening inflammatory markers (e.g., 8-isoprostane) independent of weight loss.\n- Ramadan-style fasting studies show decreases in CRP and some pro-inflammatory cytokines, though protocols and results vary.\n\n5. Cellular repair & aging markers (more preliminary)\n- Human data are modest, but IF consistently increases ketone bodies and activates pathways linked to autophagy and stress resistance (review: de Cabo & Mattson, NEJM 2019).\n- In animals, IF extends lifespan and improves neuroprotection; in humans, we mostly see surrogate markers rather than demonstrated lifespan extension yet.\n\n6. Brain function (emerging evidence)\n- Short-term human studies show improved subjective alertness and sometimes executive function during fasting; robust long-term cognitive RCTs are lacking.\n\nKey caveats:\n- Benefits are strongest for overweight/insulin-resistant individuals; effects are smaller in lean, metabolically healthy people.\n- Not advised without supervision for pregnancy, eating disorders, some medications (especially insulin/sulfonylureas), or significant underweight.',
        issue: [
          'Presents animal lifespan/neuroprotection data alongside human RCT results without clearly distinguishing',
          'Missing the 2024 AHA cardiovascular risk finding entirely',
        ],
      },
      {
        model: 'Gemini 3',
        color: '#4285f4',
        badge: 'Outdated Primary Source',
        badgeColor: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
        excerpt: 'Intermittent fasting (IF) is an eating pattern supported by substantial clinical research for improving metabolic health and cellular longevity. A 2014 review found that IF can cause 3-8% weight loss over 3-24 weeks...',
        fullAnswer: 'Intermittent fasting (IF) is an eating pattern supported by substantial clinical research for improving metabolic health and cellular longevity. Here are five distinct, evidence-based benefits:\n\n1. Weight Loss and Visceral Fat Reduction\nIF is a powerful tool for weight management. A 2014 review of scientific literature found that IF can cause 3-8% weight loss over 3-24 weeks, a significant amount compared to standard calorie-restriction studies. Furthermore, participants lost 4-7% of their waist circumference, indicating a reduction in harmful visceral fat that surrounds organs. Research indicates IF increases metabolic rate by 3.6-14% due to increased norepinephrine levels, aiding in calorie burn.\n\n2. Improved Insulin Sensitivity\nIF is highly effective for type 2 diabetes prevention and management. Human studies show that intermittent fasting can reduce fasting insulin levels by 20-31% and lower fasting blood sugar by 3-6%. A study published in the World Journal of Diabetes confirmed that IF may protect against kidney damage, one of the most severe complications of diabetes.\n\n3. Cellular Repair (Autophagy)\nWhen you fast, your cells initiate a waste-removal process called autophagy. This involves cells breaking down and metabolizing broken and dysfunctional proteins that build up inside cells over time. Increased autophagy is linked to protection against cancer and neurodegenerative diseases. The 2016 Nobel Prize in Physiology or Medicine was awarded for discovering the mechanisms of autophagy, underscoring its biological importance.\n\n4. Enhanced Heart Health\nIF improves numerous risk factors for heart disease. Animal studies consistently demonstrate that IF improves blood pressure, total and LDL cholesterol, blood triglycerides, and inflammatory markers. A study involving obese adults showed that alternate-day fasting reduced LDL cholesterol by 20-25% and triglycerides by 15-30% after eight weeks.\n\n5. Brain Health and Neuroprotection\nFasting triggers a metabolic switch that boosts brain health. It increases levels of a brain hormone called Brain-Derived Neurotrophic Factor (BDNF). Low levels of BDNF are implicated in depression and Alzheimer\'s disease. Animal studies indicate that IF increases the growth of new nerve cells and may protect against brain damage caused by strokes.',
        issue: [
          'Relies on a 2014 review as its primary source',
          'Heart health section mixes animal and human findings without clear distinction',
          'Misses the 2024 AHA cardiovascular risk concerns entirely',
        ],
      },
      {
        model: 'Claude Sonnet 4.5',
        color: '#d97706',
        badge: 'Overstated Animal Data',
        badgeColor: 'bg-red-500/15 text-red-400 border-red-500/20',
        excerpt: 'Intermittent fasting (IF) has substantial scientific evidence supporting multiple health benefits. Meta-analyses show IF produces 3-8% weight loss. Animal studies show 50-400% BDNF increases and 30-40% lifespan extension...',
        fullAnswer: 'Intermittent fasting (IF) has substantial scientific evidence supporting multiple health benefits:\n\nWeight Loss and Metabolic Health\n- Weight reduction: Meta-analyses show IF produces 3-8% weight loss over 3-24 weeks, with particular effectiveness for visceral fat reduction\n- Insulin sensitivity: Studies demonstrate 20-31% reduction in fasting insulin levels and 3-6% decrease in blood sugar, reducing type 2 diabetes risk\n- Metabolic rate: Short-term fasting (48 hours or less) can increase metabolic rate by 3.6-14% through norepinephrine elevation\n\nCardiovascular Benefits\nResearch published in the New England Journal of Medicine (2019) shows:\n- LDL cholesterol reduction of 10-15%\n- Triglyceride decrease of 20-30%\n- Blood pressure improvements of 4-7 mmHg systolic\n- Reduced inflammatory markers (CRP, IL-6)\n\nCellular and Brain Health\n- Autophagy: Fasting triggers cellular "cleanup" processes, removing damaged proteins and organelles (Nobel Prize-winning research, 2016)\n- Brain-derived neurotrophic factor (BDNF): Animal studies show 50-400% increases, supporting neuron growth and protection\n- Cognitive function: Human trials indicate improvements in memory and mental clarity, potentially reducing Alzheimer\'s risk\n\nLongevity Markers\n- Studies in Cell Metabolism (2018) demonstrate improved markers associated with longevity\n- Reduced oxidative stress and inflammation\n- Enhanced DNA repair mechanisms\n- Animal models show 30-40% lifespan extension (human translation unclear)\n\nCancer Risk\n- Preliminary evidence suggests IF may reduce cancer risk through:\n  - Lower IGF-1 levels (growth hormone linked to cancer)\n  - Enhanced immune function\n  - Reduced cell proliferation rates\n\nImportant Caveats\nMost robust evidence comes from 16:8 or 5:2 fasting protocols. Benefits appear strongest when combined with healthy eating patterns. Not suitable for pregnant women, children, or those with certain medical conditions.',
        issue: [
          'Presents dramatic animal-model figures prominently ("50-400% BDNF increases," "30-40% lifespan extension")',
          'No adequate caveats that these don\'t translate to humans',
          'Cancer section lacks human trial support',
        ],
      },
      {
        model: 'Vettis Council',
        color: '#D4A574',
        badge: 'Verified Consensus',
        badgeColor: 'bg-verdict-primary/15 text-verdict-primary border-verdict-primary/20',
        excerpt: 'Council consensus from 3 agents: Strong evidence for weight loss (3-8% over 3-24 weeks, per multiple meta-analyses) and insulin sensitivity improvement. However, the 2024 AHA study flagged 91% higher cardiovascular mortality risk with <8hr eating windows -- a critical finding all three agents missed.',
        fullAnswer: 'VETTIS COUNCIL CONSENSUS REPORT\nConfidence: High | Agents: 3/3 Reached Consensus | Debate Rounds: 3\n\nThe council reviewed claims from three independent analyses and resolved several factual conflicts:\n\nTier 1 -- Strong Evidence (All Agents Agreed):\n- Weight loss: 3-8% body weight reduction over 3-24 weeks. Supported by meta-analysis of 27 RCTs (Harris et al., Nutrients 2018) and confirmed by Trepanowski et al. (JAMA Intern Med 2017). Effect size is comparable to continuous calorie restriction.\n- Insulin sensitivity: Meaningful improvement, especially in overweight/pre-diabetic populations. The 2018 Cell Metabolism study (Sutton et al.) showed benefits independent of weight loss -- all three agents cited this correctly.\n- Inflammation: Moderate reductions in CRP and IL-6 markers. Clinically meaningful for individuals with elevated baseline inflammation.\n\nTier 2 -- Emerging Evidence (Debate Required):\n- Autophagy: Real biological mechanism, but Agent-Anthropic overstated the human implications. Most autophagy data comes from animal models. The council downgraded this from "proven" to "promising."\n- Cognitive benefits: Limited human RCT data. Agent-Gemini and Agent-Anthropic both cited animal BDNF data without sufficient human evidence caveats. The council flagged this distinction.\n\nCRITICAL FINDING -- Resolved via Debate:\nNone of the three agents mentioned the March 2024 American Heart Association study (presented at AHA Epidemiology Conference) which found that adults following a <8-hour eating window had a 91% higher risk of cardiovascular death compared to those eating over 12-16 hours. This finding was surfaced by the council\'s cross-referencing module and fundamentally changes the risk profile of aggressive IF protocols.\n\nDEBUNKED CLAIMS:\n1. Agent-Anthropic reported "50-400% BDNF increases" and "30-40% lifespan extension" -- the council verified these are exclusively from animal models (rodent studies, primarily Mattson et al.). No equivalent human data exists.\n2. Agent-Gemini cited heart health data mixing animal and human findings without distinction. The council separated these evidence levels.\n3. Agent-Gemini relied on a 2014 review as its primary source -- the council noted this is a decade old and missing critical newer findings.\n\nActionable Summary:\nIF is well-supported for weight management and metabolic health in overweight/insulin-resistant individuals. However, the 2024 AHA cardiovascular finding introduces a serious caution for aggressive fasting protocols (<8hr windows). The 12:12 or 14:10 protocols appear safer than 16:8 based on current evidence. Consult a physician if you have cardiovascular risk factors.\n\nUncertainty Disclosure: Long-term (10+ year) human outcome data for IF remains insufficient. The council cannot verify claims about cancer prevention or lifespan extension in humans.',
        issue: [
          'Caught overstated animal data from one agent',
          'Flagged outdated primary sources from another',
          'Surfaced the critical 2024 AHA cardiovascular risk finding all three agents missed',
        ],
        isVettis: true,
      },
    ],
  },
];

function LandingStat({ value, suffix = '', decimals = 0 }: { value: number; suffix?: string; decimals?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <span ref={ref}>
      {inView ? <CountUp end={value} duration={2} decimals={decimals} suffix={suffix} /> : <span>0{suffix}</span>}
    </span>
  );
}

function BenchRadarTip({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }> }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface/95 border border-border-subtle rounded-lg px-3 py-2 text-xs shadow-xl backdrop-blur-sm space-y-1">
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-text-secondary">{p.name}:</span>
          <span className="text-text-primary font-semibold">{p.value || '--'}</span>
        </div>
      ))}
    </div>
  );
}

export function VerdictLandingPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { resolvedTheme, setMode } = useTheme();
  const [headlineReady, setHeadlineReady] = useState(false);
  const [isFirstQuery, setIsFirstQuery] = useState(true);
  const [fingerprintChecked, setFingerprintChecked] = useState(false);
  const [queriesRemaining, setQueriesRemaining] = useState<number>(3); // Default to max
  const [maxFreeQueries, setMaxFreeQueries] = useState<number>(3);
  const [requiresSignup, setRequiresSignup] = useState(false);
  const [signupCredits, setSignupCredits] = useState<number>(150);
  // A/B Test state (variant stored for potential future use/display)
  const [_abTestVariant, setAbTestVariant] = useState<'sealed_verdict' | 'free_queries'>('free_queries');
  const [showSealedOverlay, setShowSealedOverlay] = useState(false);
  const [isLeftPanelExpanded, setIsLeftPanelExpanded] = useState(false);
  const [activeComparisonIdx, setActiveComparisonIdx] = useState(0);
  const [expandedAnswer, setExpandedAnswer] = useState<ComparisonAnswer | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Responsive hooks
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;

  // Browser fingerprint for Sealed Verdict flow
  const { visitorId: fingerprintId, loading: fingerprintLoading } = useFingerprint();

  // Credits hook for logged-in users
  const { balanceData: creditBalance, isLoading: creditsLoading } = useCredits();

  // Financial analysis query - stored separately since financial flow uses different state
  const [financialQuery, setFinancialQuery] = useState<string>('');

  // Research session state management (only for free/anonymous users)
  const {
    session,
    submitFreeQuery,
    submitFingerprintQuery,
    unsealVerdict,
    reset: resetSession,
    isActive: isResearchActive,
    isComplete: isResearchComplete,
    isSealed: isVerdictSealed,
    hasError,
  } = useResearchSession({
    isAuthenticated: false, // Always use free-tier flow on landing page
    onComplete: (reportId) => {
      console.log('Research complete:', reportId);
    },
    onError: (error) => {
      console.error('Research error:', error);
    },
  });

  // Financial analysis streaming hook - used for financial deep dives
  const {
    state: financialState,
    isConnecting: isFinancialConnecting,
    startFreeTierAnalysis,
    reset: resetFinancialAnalysis,
  } = useFinancialStream({
    onComplete: () => {
      console.log('Financial analysis complete');
    },
    onError: (error) => {
      console.error('Financial analysis error:', error);
    },
  });

  // Track if we're in the process of starting a financial analysis (before SSE connects)
  const [isFinancialLoading, setIsFinancialLoading] = useState(false);

  // Check fingerprint query count when fingerprint is loaded
  useEffect(() => {
    if (fingerprintLoading || !fingerprintId || fingerprintChecked) return;

    const checkFingerprint = async () => {
      try {
        const status = await api.checkFingerprintStatus(fingerprintId);
        setIsFirstQuery(status.is_first_query);
        setQueriesRemaining(status.queries_remaining);
        setMaxFreeQueries(status.max_free_queries);
        setRequiresSignup(status.requires_signup);
        setSignupCredits(status.signup_credits);
        // A/B Test fields
        setAbTestVariant(status.ab_test_variant);
        setShowSealedOverlay(status.show_sealed_overlay);
        setFingerprintChecked(true);

        // Log A/B test assignment for tracking
        console.log(`A/B Test: variant=${status.ab_test_variant}, max_queries=${status.max_free_queries}, show_sealed=${status.show_sealed_overlay}`);
      } catch (err) {
        console.error('Failed to check fingerprint status:', err);
        // Default to first query on error (most permissive - Variant B)
        setIsFirstQuery(true);
        setQueriesRemaining(3);
        setRequiresSignup(false);
        setAbTestVariant('free_queries');
        setShowSealedOverlay(false);
        setFingerprintChecked(true);
      }
    };

    checkFingerprint();
  }, [fingerprintId, fingerprintLoading, fingerprintChecked]);

  // Trigger headline scramble animation after mount
  useEffect(() => {
    const timer = setTimeout(() => setHeadlineReady(true), 300);
    return () => clearTimeout(timer);
  }, []);



  // Determine if we should show collapsed left panel (research active but user hasn't expanded it)
  const showCollapsedLeftPanel = (isResearchActive || isResearchComplete) && !user && !isVerdictSealed && !isLeftPanelExpanded;

  // Lock page scroll ONLY when Research Blueprint takes full screen (left panel collapsed)
  // When left panel is expanded (query box visible), allow scrolling and show footer
  useEffect(() => {
    if (showCollapsedLeftPanel && !isSmallScreen) {
      // Disable body scroll only when blueprint is full screen on desktop
      // On mobile, allow natural page scroll since there's no collapsed panel visible
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable body scroll when left panel is visible or research not active
      document.body.style.overflow = '';
      if (!isResearchActive && !isResearchComplete) {
        setIsLeftPanelExpanded(false);
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [showCollapsedLeftPanel, isResearchActive, isResearchComplete, isSmallScreen]);

  // Handle logout - stay on current page (landing page is public)
  const handleLogout = () => {
    logout();
  };

  // Helper: Detect the effective research mode using backend LLM classifier.
  // Replaces client-side regex patterns which could disagree with the backend.
  const detectEffectiveMode = useCallback(async (query: string, mode: ResearchMode): Promise<ResearchMode> => {
    if (mode !== 'auto') return mode;
    try {
      const detection = await api.detectResearchMode(query, mode);
      console.log(
        `[Mode Detection] "${query.slice(0, 50)}..." -> ${detection.effective_mode}`,
        `(confidence=${detection.confidence.toFixed(2)})`
      );
      return detection.effective_mode as ResearchMode;
    } catch (err) {
      console.warn('Mode detection failed, defaulting to general:', err);
      return 'general';
    }
  }, []);

  // Handle new research (reset both general and financial state)
  const handleNewResearch = useCallback(() => {
    resetFinancialAnalysis();
    setFinancialQuery('');
    resetSession();
  }, [resetFinancialAnalysis, resetSession]);

  // Handle free-tier query submission (legacy email flow)
  // Note: Free users are always restricted to 'fast' intensity on the backend
  const handleFreeSubmit = async (query: string, email: string, turnstileToken: string, intensity: ResearchIntensity, mode: ResearchMode) => {
    // Resolve effective mode using backend LLM classifier
    const effectiveMode = await detectEffectiveMode(query, mode);

    if (effectiveMode === 'financial') {
      setFinancialQuery(query);
      setIsFinancialLoading(true);
      try {
        // First call planning endpoint to track usage and get tasks
        const response = await api.requestFreeReport({
          topic: query,
          turnstile_token: turnstileToken,
          email,
          intensity: 'fast', // Free tier always fast
          mode: 'financial', // Force financial mode
        });

        if (response.success) {
          // Use free-tier financial stream with fingerprint tracking
          startFreeTierAnalysis({
            topic: query,
            turnstileToken,
            email,
            tasks: response.tasks || [],
          });
        } else {
          console.error('Failed to start financial analysis:', response.message);
        }
      } catch (err) {
        console.error('Financial analysis error:', err);
      } finally {
        setIsFinancialLoading(false);
      }
      return;
    }
    // Standard research flow (pass resolved mode)
    await submitFreeQuery(query, email, turnstileToken, intensity, effectiveMode);
  };

  // Handle fingerprint-based query submission (Sealed Verdict flow)
  // Note: Free users are always restricted to 'fast' intensity on the backend
  const handleFingerprintSubmit = async (query: string, fpId: string, turnstileToken: string, intensity: ResearchIntensity, mode: ResearchMode) => {
    // Resolve effective mode using backend LLM classifier
    const effectiveMode = await detectEffectiveMode(query, mode);

    if (effectiveMode === 'financial') {
      setFinancialQuery(query);
      setIsFinancialLoading(true);
      try {
        // First call planning endpoint to track fingerprint usage and get tasks
        const response = await api.requestFreeReport({
          topic: query,
          turnstile_token: turnstileToken,
          fingerprint_id: fpId,
          intensity: 'fast', // Free tier always fast
          mode: 'financial', // Force financial mode
        });

        if (response.success) {
          // Use free-tier financial stream with fingerprint tracking
          startFreeTierAnalysis({
            topic: query,
            turnstileToken,
            fingerprintId: fpId,
            tasks: response.tasks || [],
          });
        } else {
          console.error('Failed to start financial analysis:', response.message);
        }
      } catch (err) {
        console.error('Financial analysis error:', err);
      } finally {
        setIsFinancialLoading(false);
      }
      return;
    }
    // Standard research flow (pass resolved mode)
    await submitFingerprintQuery(query, fpId, turnstileToken, intensity, effectiveMode);
  };

  // Handle unsealing a sealed verdict
  const handleUnseal = async (email: string, turnstileToken: string) => {
    await unsealVerdict(email, turnstileToken);
  };

  return (
    <div className="min-h-screen bg-background text-text-primary verdict-grain verdict-vignette selection:bg-verdict-primary/30">
      <SEO />
      {/* Ambient Lighting - Gold spotlight for "Truth emerging from darkness" */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-verdict-primary/8 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-30%] right-[-10%] w-[600px] h-[600px] bg-verdict-primary/5 blur-[100px] rounded-full" />
      </div>

      {/* Navigation - Edge to edge */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border-subtle">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img src="/octoverdict-no-bg.png" alt="Vettis" className="w-5 h-5 sm:w-6 sm:h-6 object-contain" />
              <span className="text-base sm:text-lg font-semibold tracking-tight">
                <span className="text-verdict-primary">Vettis</span>
              </span>
            </div>

            {/* Desktop Center Nav Links */}
            <div className="hidden sm:flex items-center gap-8 text-sm text-text-secondary">
              <button onClick={() => navigate('/')} className="text-text-primary">Home</button>
              <button onClick={() => navigate('/benchmark')} className="hover:text-text-primary transition-colors">Benchmark</button>
              <button onClick={() => navigate('/docs')} className="hover:text-text-primary transition-colors">Docs</button>
              <button onClick={() => navigate('/pricing')} className="hover:text-text-primary transition-colors">Pricing</button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden p-2 rounded-lg hover:bg-surface-light transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-text-secondary" />
              ) : (
                <Menu className="w-5 h-5 text-text-secondary" />
              )}
            </button>

            {/* Desktop Auth Buttons */}
            <div className="hidden sm:flex items-center gap-3">
              <button
                onClick={() => setMode(resolvedTheme === 'dark' ? 'light' : 'dark')}
                className="p-2 rounded-lg hover:bg-surface-light transition-colors text-text-secondary hover:text-text-primary"
                aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {resolvedTheme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              {user ? (
                <>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="px-4 py-1.5 text-sm border border-border-subtle hover:bg-surface-light transition-colors rounded-lg"
                  >
                    Console
                  </button>
                  <UserMenu
                    user={user}
                    onLogout={handleLogout}
                    showConsoleLink
                  />
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-4 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Log In
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="px-4 py-1.5 text-sm bg-verdict-primary/10 border border-verdict-primary/30 text-verdict-primary hover:bg-verdict-primary/20 transition-colors rounded-lg"
                  >
                    Try it now
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="sm:hidden py-4 border-t border-border-subtle animate-fade-in">
              <div className="flex flex-col gap-2">
                <div className="px-4 pb-2 flex items-center justify-between">
                  <span className="text-xs text-text-muted uppercase tracking-wider">Theme</span>
                  <button
                    onClick={() => setMode(resolvedTheme === 'dark' ? 'light' : 'dark')}
                    className="p-2 rounded-lg hover:bg-surface-light transition-colors text-text-secondary hover:text-text-primary"
                    aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
                  >
                    {resolvedTheme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  </button>
                </div>
                {user ? (
                  <>
                    {/* User info */}
                    <div className="px-4 py-2 mb-2">
                      <p className="text-xs text-text-muted">Signed in as</p>
                      <p className="text-sm text-text-primary truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                      className="w-full px-4 py-2.5 text-sm text-text-primary hover:bg-surface-light transition-colors rounded-lg text-left"
                    >
                      Home
                    </button>
                    <button
                      onClick={() => { navigate('/benchmark'); setMobileMenuOpen(false); }}
                      className="w-full px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors rounded-lg text-left"
                    >
                      Benchmark
                    </button>
                    <button
                      onClick={() => { navigate('/docs'); setMobileMenuOpen(false); }}
                      className="w-full px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors rounded-lg text-left"
                    >
                      Docs
                    </button>
                    <button
                      onClick={() => {
                        navigate('/dashboard');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-primary hover:text-text-primary hover:bg-surface-light transition-colors rounded-lg"
                    >
                      <Terminal className="w-4 h-4" />
                      Console
                    </button>
                    <button
                      onClick={() => {
                        navigate('/preferences?tab=profile');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-primary hover:text-text-primary hover:bg-surface-light transition-colors rounded-lg"
                    >
                      <User className="w-4 h-4" />
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        navigate('/preferences');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-primary hover:text-text-primary hover:bg-surface-light transition-colors rounded-lg"
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </button>
                    <div className="border-t border-border-subtle mt-2 pt-2">
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors rounded-lg"
                      >
                        <LogOut className="w-4 h-4" />
                        Log out
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                      className="w-full px-4 py-2.5 text-sm text-text-primary hover:bg-surface-light transition-colors rounded-lg text-left"
                    >
                      Home
                    </button>
                    <button
                      onClick={() => { navigate('/benchmark'); setMobileMenuOpen(false); }}
                      className="w-full px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors rounded-lg text-left"
                    >
                      Benchmark
                    </button>
                    <button
                      onClick={() => { navigate('/docs'); setMobileMenuOpen(false); }}
                      className="w-full px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors rounded-lg text-left"
                    >
                      Docs
                    </button>
                    <button
                      onClick={() => {
                        navigate('/login');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors rounded-lg text-left"
                    >
                      Log In
                    </button>
                    <button
                      onClick={() => {
                        navigate('/dashboard');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full px-4 py-2.5 text-sm bg-verdict-primary/10 border border-verdict-primary/30 text-verdict-primary hover:bg-verdict-primary/20 transition-colors rounded-lg text-left"
                    >
                      Try it now
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section - Responsive flex layout */}
      <section className={cn(
        "relative flex flex-col",
        // On mobile during research, don't force min-h-screen
        isSmallScreen && (isResearchActive || isResearchComplete || isVerdictSealed)
          ? "min-h-0 pt-16 sm:pt-20"
          : "min-h-screen pt-16 sm:pt-20 justify-center"
      )}>
        {/* Responsive padding */}
        <div className={cn(
          "w-full px-4 sm:px-6 lg:pl-8 xl:pl-12",
          // Only remove right padding on large screens when showing research
          !isSmallScreen && (isResearchActive || isResearchComplete) ? "lg:pr-0" : "lg:pr-8 xl:pr-12"
        )}>
          {/* Dynamic Grid: Stacked on mobile, side-by-side on desktop */}
          <div className={cn(
            "grid transition-all duration-500",
            // Mobile: single column, Tablet+: 12-column grid
            isSmallScreen ? "grid-cols-1 gap-6" : "md:grid-cols-12 items-center",
            showCollapsedLeftPanel ? "gap-4" : "gap-8 lg:gap-12"
          )}>
            {/* Left Column - Collapsed when research active (desktop only) */}
            {showCollapsedLeftPanel && !isSmallScreen ? (
              // Collapsed Left Panel - Just back arrow and query (desktop only)
              <div className="hidden md:col-span-1 md:flex flex-col items-center justify-start pt-20 h-[calc(100vh-64px)]">
                <button
                  onClick={() => setIsLeftPanelExpanded(true)}
                  className="group flex flex-col items-center gap-3 p-3 rounded-xl hover:bg-surface-light transition-colors"
                  title="Show input panel"
                >
                  <div className="w-10 h-10 rounded-full bg-verdict-primary/20 border border-verdict-primary/30 flex items-center justify-center group-hover:bg-verdict-primary/30 transition-colors">
                    <ArrowLeft className="w-5 h-5 text-verdict-primary" />
                  </div>
                  <div className="writing-mode-vertical text-xs text-text-muted max-h-[200px] overflow-hidden">
                    <Search className="w-3 h-3 mb-2 mx-auto text-text-muted" />
                    <span className="line-clamp-3">{session.query}</span>
                  </div>
                </button>
              </div>
            ) : (
              // Full Left Column - Content
              <div className={cn(
                "flex flex-col justify-center",
                // Mobile: full width, always visible
                // Desktop: 4 cols when not collapsed (reduced to give more space to video)
                isSmallScreen ? "py-6 sm:py-8" : "md:col-span-5 py-12 md:py-16",
                // On mobile, hide left column when research is active
                isSmallScreen && (isResearchActive || isResearchComplete || isVerdictSealed) && "hidden"
              )}>
                <SectionLabel className="mb-4 sm:mb-6">DECISION INTELLIGENCE</SectionLabel>

                {/* Scramble Text Headline - Responsive sizing */}
                <h1 className="verdict-headline mb-6 sm:mb-8 text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
                  <ScrambleText
                    text="Don't Guess."
                    trigger={headlineReady}
                    scrambleSpeed={50}
                    delay={0}
                    loop={12000}
                    className="block"
                  />
                  <ScrambleText
                    text="Vettis."
                    trigger={headlineReady}
                    scrambleSpeed={50}
                    delay={400}
                    loop={12000}
                    className="block"
                    finalColor="text-verdict-primary"
                  />
                </h1>

                {/* Subtitle - Decision Engine positioning */}
                <p className="text-text-primary text-base sm:text-lg md:text-xl leading-relaxed mb-4 sm:mb-5 max-w-lg">
                  The{' '}
                  <span className="text-verdict-primary font-semibold">High-Stake Decision Engine</span>.
                </p>

                {/* Body copy */}
                <p className="text-text-primary text-sm sm:text-base leading-relaxed mb-10 sm:mb-12 max-w-lg">
                  When the stakes are high — your portfolio, your health, your strategy —{' '}
                  a single AI isn't enough.{' '}
                  <span className="text-verdict-primary/90">Vettis</span>{' '}
                  deploys rival AI agents{' '}
                  <span className="text-text-secondary">(OpenAI, Anthropic, Google, etc.)</span>{' '}
                  that research, debate, and deliver a{' '}
                  <span className="text-text-primary font-medium">verified consensus</span>,{' '}
                  so you make the right call.
                </p>

              {/* For logged-in users: Show Dashboard CTA instead of research input */}
              {user ? (
                <div className="w-full max-w-xl mt-2">
                  <div className="relative">
                    {/* God-ray glow effect */}
                    <div className="absolute -inset-1 rounded-2xl blur-xl bg-gradient-to-r from-verdict-primary/0 via-verdict-primary/40 to-verdict-primary/0 opacity-40" />

                    <div className={cn(
                      'relative bg-surface border-2 border-verdict-primary/30 rounded-2xl',
                      'shadow-2xl shadow-black/50',
                      'p-4 sm:p-6'
                    )}>
                      <p className="text-text-secondary text-xs sm:text-sm mb-2 truncate">
                        Welcome back, <span className="text-text-primary font-medium">{user.email}</span>
                      </p>

                      {/* Credits remaining indicator */}
                      {creditsLoading ? (
                        <p className="text-text-muted text-xs sm:text-sm mb-3 sm:mb-4">Loading credits...</p>
                      ) : creditBalance && creditBalance.credit_balance !== undefined ? (
                        <p className="text-verdict-primary/80 text-xs sm:text-sm mb-3 sm:mb-4 font-medium">
                          {creditBalance.is_unlimited ? (
                            <span className="text-verdict-primary">Unlimited credits</span>
                          ) : creditBalance.credit_balance > 0 ? (
                            <>You have <span className="text-verdict-primary">{creditBalance.credit_balance.toLocaleString()}</span> credits remaining</>
                          ) : (
                            <span className="text-amber-400">No credits remaining. Purchase more to continue.</span>
                          )}
                        </p>
                      ) : (
                        <div className="mb-3 sm:mb-4" />
                      )}

                      <p className="text-text-primary text-sm sm:text-base mb-4 sm:mb-6">
                        Generate custom research reports from your dashboard with full access to all features.
                      </p>
                      <button
                        onClick={() => navigate('/dashboard')}
                        className={cn(
                          'flex items-center justify-center gap-2 w-full px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl',
                          'bg-verdict-primary/10 hover:bg-verdict-primary/20',
                          'border border-verdict-primary/30 text-verdict-primary',
                          'font-medium transition-all duration-300 text-sm sm:text-base'
                        )}
                      >
                        Go to Dashboard
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Council Badge + Model Carousel for logged-in users */}
                  <div className="mt-6 sm:mt-10 space-y-3 sm:space-y-4">
                    <CouncilBadge />
                    <ModelCarousel />
                  </div>
                </div>
              ) : requiresSignup ? (
                // User has exhausted free queries - show signup CTA
                <div className="w-full max-w-xl mt-2">
                  <div className="relative">
                    {/* God-ray glow effect */}
                    <div className="absolute -inset-1 rounded-2xl blur-xl bg-gradient-to-r from-verdict-primary/0 via-verdict-primary/40 to-verdict-primary/0 opacity-40" />

                    <div className={cn(
                      'relative bg-surface border-2 border-verdict-primary/30 rounded-2xl',
                      'shadow-2xl shadow-black/50',
                      'p-6 sm:p-8'
                    )}>
                      <h3 className="text-xl sm:text-2xl font-semibold text-text-primary mb-3">
                        You've used all {maxFreeQueries} free queries
                      </h3>
                      <p className="text-text-secondary text-sm sm:text-base mb-6 leading-relaxed">
                        Sign up now to get <span className="text-verdict-primary font-semibold">{signupCredits} free credits</span> and
                        unlock access to better AI models for more accurate research.
                      </p>
                      <button
                        onClick={() => navigate('/signup')}
                        className={cn(
                          'flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl',
                          'bg-verdict-primary/20 hover:bg-verdict-primary/30',
                          'border border-verdict-primary/50 text-verdict-primary',
                          'font-medium transition-all duration-300 text-base'
                        )}
                      >
                        Sign Up for Free
                        <ArrowRight className="w-4 h-4" />
                      </button>
                      <p className="text-text-muted text-xs mt-4 text-center">
                        Already have an account?{' '}
                        <button
                          onClick={() => navigate('/login')}
                          className="text-verdict-primary hover:underline"
                        >
                          Log in
                        </button>
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Research Input - Fingerprint-based free query flow */}
                  <ResearchInput
                    onSubmitFree={handleFreeSubmit}
                    onSubmitFingerprint={handleFingerprintSubmit}
                    fingerprintId={fingerprintId}
                    isFirstQuery={isFirstQuery}
                    isAuthenticated={false}
                    isSubmitting={session.phase === 'submitting'}
                    isResearchActive={isResearchActive}
                    error={hasError ? session.error : null}
                    className="w-full max-w-xl"
                    showIntensitySelector={false}
                  />

                  {/* Trust signal + free tier messaging */}
                  {!isResearchActive && !isVerdictSealed && queriesRemaining === maxFreeQueries && (
                    <p className="text-text-muted text-xs mt-3 text-center max-w-xl flex items-center justify-center gap-1.5">
                      <Shield className="w-3 h-3 text-text-muted" />
                      {maxFreeQueries} free analyses. No signup. No credit card.
                      <span className="text-text-muted mx-1">|</span>
                      Queries encrypted &amp; never used to train AI.
                    </p>
                  )}

                  {/* Free queries remaining indicator - show when not researching */}
                  {!isResearchActive && !isVerdictSealed && queriesRemaining < maxFreeQueries && (
                    <p className="text-text-muted text-xs sm:text-sm mt-3 text-center max-w-xl">
                      {queriesRemaining > 0 ? (
                        <>
                          <span className="text-verdict-primary/80">{queriesRemaining}</span>
                          {' '}free {queriesRemaining === 1 ? 'query' : 'queries'} remaining.{' '}
                          {showSealedOverlay && (
                            <span className="text-amber-500/80">Next query will be sealed. </span>
                          )}
                          <button onClick={() => navigate('/signup')} className="text-verdict-primary hover:underline">
                            Sign up for {signupCredits} credits
                          </button>
                        </>
                      ) : null}
                    </p>
                  )}

                  {/* Mobile CTA button — prominent on small screens where input may not stand out */}
                  {!isResearchActive && !isVerdictSealed && (
                    <button
                      onClick={() => {
                        const input = document.querySelector('textarea[aria-label="Research query"]') as HTMLTextAreaElement;
                        input?.focus();
                      }}
                      className="sm:hidden mt-4 w-full max-w-xl flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-verdict-primary text-black font-medium text-sm"
                    >
                      <Sparkles className="w-4 h-4" />
                      Run Free Analysis
                    </button>
                  )}

                  {/* Council Badge + Model Carousel - hide during active research */}
                  {!isResearchActive && !isVerdictSealed && (
                    <div className="mt-6 sm:mt-10 space-y-3 sm:space-y-4">
                      <CouncilBadge />
                      <ModelCarousel />
                    </div>
                  )}
                </>
              )}
              </div>
            )}

            {/* Right Column - Visualization / Active Research / Results / Sealed Verdict */}
            {/* Mobile: shows below the left column, Desktop: side by side */}
            <div className={cn(
              "flex items-stretch transition-all duration-500 self-stretch",
              // Mobile: full width, always visible during research
              isSmallScreen
                ? "w-full justify-center"
                : showCollapsedLeftPanel
                  ? "md:col-span-11 justify-start h-[calc(100vh-64px)] overflow-hidden"
                  : "md:col-span-7",
              // Tablet+: hidden by default, shown via md:flex
              !isSmallScreen && "hidden md:flex",
              // Justify content based on state
              (isResearchActive || isResearchComplete) && !user && !isVerdictSealed
                ? 'justify-start'
                : 'justify-center'
            )}>
              {/* For logged-in users: Always show the visualization with demo (hide on mobile when idle) */}
              {user ? (
                <CouncilVisualizationWithDemo
                  className={cn(
                    "w-full aspect-video",
                    isSmallScreen ? "max-w-full hidden" : "max-w-none"
                  )}
                  isActive={false}
                  phase="idle"
                  disableAutoSwap={false}
                />
              ) : isVerdictSealed ? (
                // Sealed Verdict - Email capture to unseal (2nd+ query)
                <div className={cn(
                  "w-full verdict-card rounded-2xl",
                  isSmallScreen ? "max-w-full px-4" : "max-w-[600px]"
                )}>
                  <SealedVerdictOverlay
                    query={session.query}
                    headline={session.sealedHeadline || 'The Council has reached a High Conviction verdict.'}
                    onUnseal={handleUnseal}
                    isUnsealing={session.phase === 'submitting'}
                    error={hasError ? session.error : null}
                  />
                </div>
              ) : (isFinancialLoading || isFinancialConnecting) ? (
                // Financial Deep Dive - Loading state
                // Shown while waiting for planning endpoint and SSE connection
                <div className={cn(
                  "flex flex-col items-center justify-center gap-6 w-full py-16",
                  isSmallScreen ? "px-4" : "max-w-md mx-auto"
                )}>
                  <div className="relative">
                    {/* Animated rings */}
                    <div className="absolute inset-0 rounded-full border-2 border-verdict-primary/20 animate-ping" style={{ animationDuration: '2s' }} />
                    <div className="absolute inset-2 rounded-full border-2 border-verdict-primary/30 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }} />
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 border border-verdict-primary/40 flex items-center justify-center">
                      <svg className="w-8 h-8 text-verdict-primary animate-spin" style={{ animationDuration: '3s' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2v4m0 12v4m10-10h-4M6 12H2m15.07-5.07l-2.83 2.83M9.76 14.24l-2.83 2.83m11.31 0l-2.83-2.83M9.76 9.76L6.93 6.93" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center space-y-2">
                    <h3 className="text-lg font-medium text-text-primary">Initializing Financial Deep Dive</h3>
                    <p className="text-sm text-text-secondary">Fetching market data and assembling Bull & Bear teams...</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <span>Connecting to analysis engine</span>
                  </div>
                </div>
              ) : financialState.phase !== 'idle' ? (
                // Financial Deep Dive - Bull vs Bear visualization
                // Shown when financial analysis is active (detected by financialState.phase)
                <div className={cn(
                  "flex items-start gap-2 w-full",
                  isSmallScreen && "px-0",
                  !isSmallScreen && showCollapsedLeftPanel && "h-full"
                )}>
                  {/* Collapse arrow - only show when left panel is expanded during research (desktop only) */}
                  {isLeftPanelExpanded && !isSmallScreen && (
                    <button
                      onClick={() => setIsLeftPanelExpanded(false)}
                      className="flex-shrink-0 group flex flex-col items-center gap-2 p-2 rounded-xl hover:bg-surface-light transition-colors mt-4"
                      title="Expand research view"
                    >
                      <div className="w-8 h-8 rounded-full bg-verdict-primary/20 border border-verdict-primary/30 flex items-center justify-center group-hover:bg-verdict-primary/30 transition-colors">
                        <ArrowRight className="w-4 h-4 text-verdict-primary" />
                      </div>
                      <span className="writing-mode-vertical text-[10px] text-text-muted whitespace-nowrap">
                        Expand
                      </span>
                    </button>
                  )}
                  <FinancialLogicTree
                    state={financialState}
                    query={financialQuery}
                    className={cn(
                      "w-full flex-1",
                      isSmallScreen ? "max-w-full" : "max-w-none",
                      !isSmallScreen && showCollapsedLeftPanel && "h-full"
                    )}
                    onNewResearch={handleNewResearch}
                  />
                </div>
              ) : (isResearchActive || isResearchComplete || hasError) ? (
                // Logic Tree - Hierarchical visualization of research progress
                // Show LogicTree for active research, completed research (tree format), AND error states
                // Including error state preserves the research context so users can see where it failed
                <div className={cn(
                  "flex items-start gap-2 w-full",
                  isSmallScreen && "px-0",
                  !isSmallScreen && showCollapsedLeftPanel && "h-full"
                )}>
                  {/* Collapse arrow - only show when left panel is expanded during research (desktop only) */}
                  {isLeftPanelExpanded && !isSmallScreen && (
                    <button
                      onClick={() => setIsLeftPanelExpanded(false)}
                      className="flex-shrink-0 group flex flex-col items-center gap-2 p-2 rounded-xl hover:bg-surface-light transition-colors mt-4"
                      title="Expand research view"
                    >
                      <div className="w-8 h-8 rounded-full bg-verdict-primary/20 border border-verdict-primary/30 flex items-center justify-center group-hover:bg-verdict-primary/30 transition-colors">
                        <ArrowRight className="w-4 h-4 text-verdict-primary" />
                      </div>
                      <span className="writing-mode-vertical text-[10px] text-text-muted whitespace-nowrap">
                        Expand
                      </span>
                    </button>
                  )}
                  <LogicTree
                    session={session}
                    className={cn(
                      "w-full flex-1",
                      isSmallScreen ? "max-w-full" : "max-w-none",
                      !isSmallScreen && showCollapsedLeftPanel && "h-full"
                    )}
                    onNewResearch={resetSession}
                  />
                </div>
              ) : (
                // Idle - Council Visualization with Demo video swap (hide on mobile to keep page clean)
                !isSmallScreen && (
                  <CouncilVisualizationWithDemo
                    className="w-full max-w-[700px] aspect-square"
                    isActive={session.phase !== 'idle'}
                    phase={session.phase}
                    disableAutoSwap={isResearchActive || isResearchComplete || hasError}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Answer Comparison - Vettis vs Single Models */}
      {(isSmallScreen || !showCollapsedLeftPanel) && (
        <section className="relative py-20 sm:py-28">
          <div className="verdict-divider mb-20 sm:mb-28" />
          <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-7xl mx-auto">
            {/* Section header */}
            <div className="text-center mb-12 sm:mb-16">
              <SectionLabel className="mb-4">SEE THE DIFFERENCE</SectionLabel>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-5">
                Same Question. <span className="text-verdict-primary">Better Decisions.</span>
              </h2>
              <p className="text-text-secondary text-sm sm:text-base max-w-2xl mx-auto">
                Single AI models miss critical signals, contradict themselves, or bury what matters.
                Vettis catches those errors before you act on them.
              </p>
            </div>

            {/* Question selector tabs */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-10">
              {ANSWER_COMPARISONS.map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveComparisonIdx(idx)}
                  className={cn(
                    'px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 border',
                    activeComparisonIdx === idx
                      ? 'bg-verdict-primary/15 border-verdict-primary/40 text-verdict-primary'
                      : 'bg-surface/50 border-border-subtle text-text-secondary hover:text-text-primary hover:border-border-subtle'
                  )}
                >
                  <span className="hidden sm:inline">{example.tag}: </span>
                  {example.question.length > 40 ? example.question.slice(0, 40) + '...' : example.question}
                </button>
              ))}
            </div>

            {/* Active question display */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-surface/80 border border-border-subtle">
                <Search className="w-4 h-4 text-text-muted flex-shrink-0" />
                <span className="text-sm sm:text-base text-text-primary font-medium">
                  "{ANSWER_COMPARISONS[activeComparisonIdx].question}"
                </span>
              </div>
            </div>

            {/* Answer cards — swipeable carousel on mobile, grid on desktop */}
            <p className="text-center text-text-muted text-[10px] mb-2 sm:hidden">Swipe to compare</p>
            <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 sm:grid sm:grid-cols-2 xl:grid-cols-4 sm:gap-5 sm:overflow-visible sm:snap-none sm:pb-0 scrollbar-hide">
              {ANSWER_COMPARISONS[activeComparisonIdx].answers.map((answer) => (
                <button
                  key={answer.model}
                  type="button"
                  onClick={() => setExpandedAnswer(answer)}
                  className={cn(
                    'relative rounded-2xl p-5 sm:p-6 flex flex-col gap-4 transition-all duration-300 text-left cursor-pointer group',
                    'min-w-[280px] sm:min-w-0 snap-center shrink-0 sm:shrink',
                    answer.isVettis
                      ? 'bg-verdict-primary/5 verdict-glow-border'
                      : 'bg-surface/50 border border-border/50 hover:border-border-light/70'
                  )}
                  style={answer.isVettis ? { boxShadow: '0 4px 30px rgba(212,165,116,0.2)' } : undefined}
                >

                  {/* Model header */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ background: answer.color }}
                      />
                      <span className={cn(
                        'text-sm font-semibold',
                        answer.isVettis ? 'text-verdict-primary' : 'text-text-primary'
                      )}>
                        {answer.model}
                      </span>
                    </div>
                    <span className={cn(
                      'text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded-full border w-fit',
                      answer.badgeColor
                    )}>
                      {answer.badge}
                    </span>
                  </div>

                  {/* Answer excerpt */}
                  <div className={cn(
                    'text-xs sm:text-sm leading-relaxed flex-1 line-clamp-4',
                    answer.isVettis ? 'text-text-primary' : 'text-text-secondary'
                  )}>
                    &ldquo;{answer.excerpt}&rdquo;
                  </div>

                  {/* Issue / Strength callout */}
                  <div className={cn(
                    'text-[11px] sm:text-xs leading-relaxed p-3 rounded-lg border',
                    answer.isVettis
                      ? 'bg-verdict-primary/10 border-verdict-primary/20 text-verdict-primary/90'
                      : 'bg-red-500/5 border-red-500/10 text-text-muted'
                  )}>
                    <span className={cn(
                      'font-semibold block mb-1.5',
                      answer.isVettis ? 'text-verdict-primary' : 'text-red-400/80'
                    )}>
                      {answer.isVettis ? 'What Vettis did:' : 'The problem:'}
                    </span>
                    <ul className="space-y-1 pl-3.5">
                      {answer.issue.map((item, idx) => (
                        <li key={idx} className="relative pl-2 before:absolute before:left-0 before:top-[0.45em] before:w-1 before:h-1 before:rounded-full before:bg-current before:opacity-60">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Read full answer hint */}
                  <div className={cn(
                    'flex items-center gap-1.5 text-[11px] sm:text-xs font-medium transition-colors',
                    answer.isVettis
                      ? 'text-verdict-primary/60 group-hover:text-verdict-primary'
                      : 'text-text-muted group-hover:text-text-secondary'
                  )}>
                    <span>Read full answer</span>
                    <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </button>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="text-center mt-8 sm:mt-10">
              <p className="text-text-muted text-xs sm:text-sm">
                Every answer is debated, verified, and corrected before it reaches you. <span className="text-verdict-primary font-medium">That&apos;s the difference when decisions matter.</span>
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Expanded Answer Modal */}
      {expandedAnswer && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          onClick={() => setExpandedAnswer(null)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

          {/* Modal content */}
          <div
            className={cn(
              'relative w-full max-w-2xl max-h-[85vh] rounded-2xl overflow-hidden flex flex-col',
              'border shadow-2xl animate-in fade-in zoom-in-95 duration-200',
              expandedAnswer.isVettis
                ? 'bg-surface border-verdict-primary/30 shadow-verdict-primary/10'
                : 'bg-surface border-border-subtle'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Vettis highlight bar */}
            {expandedAnswer.isVettis && (
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-verdict-primary to-transparent" />
            )}

            {/* Modal header */}
            <div className="flex items-start justify-between gap-3 p-5 sm:p-6 border-b border-border-subtle flex-shrink-0">
              <div className="flex flex-col gap-2 min-w-0">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3.5 h-3.5 rounded-full flex-shrink-0"
                    style={{ background: expandedAnswer.color }}
                  />
                  <span className={cn(
                    'text-base sm:text-lg font-semibold truncate',
                    expandedAnswer.isVettis ? 'text-verdict-primary' : 'text-text-primary'
                  )}>
                    {expandedAnswer.model}
                  </span>
                </div>
                <span className={cn(
                  'text-[10px] sm:text-xs font-medium px-2.5 py-0.5 rounded-full border w-fit',
                  expandedAnswer.badgeColor
                )}>
                  {expandedAnswer.badge}
                </span>
              </div>
              <button
                onClick={() => setExpandedAnswer(null)}
                className="p-2 rounded-lg hover:bg-surface-light transition-colors flex-shrink-0 text-text-secondary hover:text-text-primary"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal body - scrollable */}
            <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
              {/* Full answer text */}
              <div className={cn(
                'text-sm sm:text-base leading-relaxed',
                expandedAnswer.isVettis ? 'text-text-primary' : 'text-text-secondary'
              )}>
                <FormattedAnswer text={expandedAnswer.fullAnswer} isVettis={expandedAnswer.isVettis} />
              </div>

              {/* Issue / Strength callout */}
              <div className={cn(
                'text-xs sm:text-sm leading-relaxed p-4 rounded-xl border',
                expandedAnswer.isVettis
                  ? 'bg-verdict-primary/10 border-verdict-primary/20 text-verdict-primary/90'
                  : 'bg-red-500/5 border-red-500/10 text-text-muted'
              )}>
                <span className={cn(
                  'font-semibold block mb-2',
                  expandedAnswer.isVettis ? 'text-verdict-primary' : 'text-red-400/80'
                )}>
                  {expandedAnswer.isVettis ? 'What Vettis did:' : 'The problem:'}
                </span>
                <ul className="space-y-1.5 pl-4">
                  {expandedAnswer.issue.map((item, idx) => (
                    <li key={idx} className="relative pl-2 before:absolute before:left-0 before:top-[0.5em] before:w-1 before:h-1 before:rounded-full before:bg-current before:opacity-60">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Blueprint Examples - Showcase all three research modes */}
      {(isSmallScreen || !showCollapsedLeftPanel) && <BlueprintExamples />}

      {/* Benchmark & Research - unified proof section */}
      {(isSmallScreen || !showCollapsedLeftPanel) && (
        <section className="relative py-20 sm:py-28">
          <div className="verdict-divider mb-20 sm:mb-28" />
          <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <SectionLabel className="mb-4">PROVEN BY DATA &amp; RESEARCH</SectionLabel>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-5">
                Measurably Better <span className="text-verdict-primary">Decisions</span>
              </h2>
              <p className="text-text-secondary text-sm sm:text-base max-w-2xl mx-auto">
                Blind head-to-head evaluations across 35 questions. Scored by independent AI judges.{' '}
                Grounded in research from{' '}
                <span className="text-text-primary font-medium">MIT, Tsinghua, and IJCAI</span>.
              </p>
            </div>

            {/* Stat row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-5">
              <div className="verdict-card-elevated rounded-2xl p-5 sm:p-6 text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-verdict-primary mb-1"><LandingStat value={75} suffix="%" /></div>
                <div className="text-xs sm:text-sm text-text-secondary">Win Rate vs Frontier*</div>
              </div>
              <div className="verdict-card-elevated rounded-2xl p-5 sm:p-6 text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-verdict-primary mb-1">6/6</div>
                <div className="text-xs sm:text-sm text-text-secondary">Dimensions Won*</div>
              </div>
              <div className="verdict-card-elevated rounded-2xl p-5 sm:p-6 text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-verdict-primary mb-1"><LandingStat value={4.4} suffix="" decimals={1} /></div>
                <div className="text-xs sm:text-sm text-text-secondary">Avg Judge Score*</div>
              </div>
              <div className="verdict-card-elevated rounded-2xl p-5 sm:p-6 text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-verdict-primary mb-1"><LandingStat value={35} suffix="" /></div>
                <div className="text-xs sm:text-sm text-text-secondary">Blind Test Questions</div>
              </div>
            </div>
            <p className="text-[10px] sm:text-xs text-text-muted text-center mb-10 sm:mb-12 max-w-2xl mx-auto leading-relaxed">
              *Blind head-to-head benchmarks across strategic and financial blueprints. Vettis Council vs.
              frontier single-model outputs (GPT-4o, Claude Sonnet). Scored by independent AI judges across 6
              dimensions: Accuracy, Depth, Actionability, Risk Awareness, Calibration, and Clarity.
            </p>

            {/* Two-column layout: Radar (left) + Bar charts (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 mb-12 sm:mb-16">
              {/* Radar chart - left column */}
              <div className="lg:col-span-5 verdict-card rounded-2xl p-5 sm:p-6 flex flex-col">
                <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5 mb-4 text-[11px] sm:text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: BENCH_CLR.frontier }} /> Frontier (single model)</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: BENCH_CLR.council }} /> Vettis</span>
                </div>
                <p className="text-[10px] text-text-muted text-center mb-2">Financial Analysis — only mode to win all 6 dimensions</p>
                <div className="flex-1 min-h-0">
                  <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                    <RadarChart data={BENCH_RADAR} cx="50%" cy="50%" outerRadius="72%">
                      <PolarGrid stroke="rgba(255,255,255,0.08)" />
                      <PolarAngleAxis dataKey="metric" tick={{ fill: 'rgb(var(--color-text-secondary))', fontSize: 10 }} />
                      <PolarRadiusAxis angle={90} domain={[60, 100]} tick={false} axisLine={false} />
                      <Radar name="Frontier" dataKey="frontier" stroke={BENCH_CLR.frontier} fill={BENCH_CLR.frontier} fillOpacity={0.08} strokeWidth={1.5} />
                      <Radar name="Vettis" dataKey="council" stroke={BENCH_CLR.council} fill={BENCH_CLR.council} fillOpacity={0.3} strokeWidth={2.5} />
                      <Tooltip content={<BenchRadarTip />} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Bar charts - right column, 2x2 grid */}
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {BENCH_BARS.map((chart) => (
                  <div key={chart.title} className="verdict-card rounded-xl p-4 sm:p-5">
                    <h3 className="text-xs sm:text-sm font-semibold text-text-primary mb-0.5">{chart.title}</h3>
                    <p className="text-[10px] sm:text-xs text-text-muted mb-3">{chart.hint}</p>
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart data={chart.data} layout="vertical" margin={{ left: 0, right: 40, top: 0, bottom: 0 }}>
                        <XAxis type="number" domain={[0, chart.max]} tick={{ fill: 'rgb(var(--color-text-muted))', fontSize: 10 }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="name" tick={BenchYTick} axisLine={false} tickLine={false} width={55} />
                        <Tooltip content={<BenchBarTip sfx={chart.sfx} />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                        <Bar dataKey="v" radius={[0, 6, 6, 0]} barSize={20} animationDuration={1200}>
                          {chart.data.map((_, idx) => (
                            <Cell key={idx} fill={BENCH_BAR_FILLS[idx]} />
                          ))}
                          <LabelList dataKey="v" position="right" fill="#e4e4e7" fontSize={11} fontWeight={600} formatter={(val: unknown) => `${val}${chart.sfx}`} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ))}
              </div>
            </div>

            {/* Research papers - academic card grid */}
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-5">
                <BookOpen className="w-4 h-4 text-verdict-primary/70" />
                <h3 className="text-sm sm:text-base font-semibold text-text-primary">Peer-Reviewed Research</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { title: 'Improving Factuality through Multiagent Debate', authors: 'Yilun Du et al.', venue: 'MIT / ICML', year: 2023, tag: 'Factuality', url: 'https://arxiv.org/abs/2305.14325' },
                  { title: 'More Agents Is All You Need', authors: 'Junyou Li et al.', venue: 'Tsinghua', year: 2024, tag: 'Scaling', url: 'https://arxiv.org/abs/2402.05120' },
                  { title: 'Encouraging Divergent Thinking through Multi-Agent Debate', authors: 'Tian Liang et al.', venue: 'ACL', year: 2023, tag: 'Debate', url: 'https://arxiv.org/abs/2305.19118' },
                  { title: 'LLM Multi-Agents: A Survey of Progress and Challenges', authors: 'Taicheng Guo et al.', venue: 'IJCAI', year: 2024, tag: 'Survey', url: 'https://arxiv.org/abs/2402.01680' },
                ].map((paper) => (
                  <a
                    key={paper.title}
                    href={paper.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="verdict-card rounded-xl p-4 sm:p-5 flex flex-col gap-2.5 hover:border-verdict-primary/30 transition-colors group"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-verdict-primary/70 bg-verdict-primary/10 border border-verdict-primary/20 rounded-full px-2.5 py-0.5 flex-shrink-0">
                        {paper.tag}
                      </span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-text-muted bg-surface-light border border-border rounded px-1.5 py-0.5">
                          arXiv
                        </span>
                        <ExternalLink className="w-3 h-3 text-text-muted group-hover:text-verdict-primary transition-colors" />
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm font-medium text-text-primary leading-snug group-hover:text-verdict-primary transition-colors">
                      {paper.title}
                    </p>
                    <p className="text-[10px] sm:text-xs text-text-muted">
                      {paper.authors} &middot; {paper.venue} {paper.year}
                    </p>
                  </a>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center space-y-4">
              <button onClick={() => navigate('/benchmark')} className="text-sm sm:text-base text-verdict-primary hover:underline transition-colors inline-flex items-center gap-1.5">
                View full benchmark &amp; research <ArrowRight className="w-4 h-4" />
              </button>
              <div>
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={cn(
                    'inline-flex items-center gap-2 px-6 py-3 rounded-xl',
                    'bg-verdict-primary/15 hover:bg-verdict-primary/25 border border-verdict-primary/30',
                    'text-verdict-primary font-medium text-sm transition-all duration-300'
                  )}
                >
                  <Sparkles className="w-4 h-4" />
                  Try with your own question — free
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Portfolio Intelligence - Early Access feature promotion */}
      {(isSmallScreen || !showCollapsedLeftPanel) && (
        <section className="relative py-20 sm:py-28">
          <div className="verdict-divider mb-20 sm:mb-28" />
          <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-7xl mx-auto">
            {/* Section header */}
            <div className="text-center mb-12 sm:mb-16">
              <SectionLabel className="mb-4">COMING SOON</SectionLabel>
              <div className="flex flex-wrap items-center justify-center gap-3 mb-5">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                  Portfolio <span className="text-verdict-primary">Intelligence</span>
                </h2>
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-full">
                  Early Access
                </span>
              </div>
              <p className="text-text-secondary text-sm sm:text-base max-w-2xl mx-auto">
                Upload your holdings. Get an adversarial analysis of every position.
                Know which stocks need your attention before the market tells you.
              </p>
            </div>

            {/* Feature grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
              {PORTFOLIO_FEATURES.map((feature) => {
                const FeatureIcon = feature.icon;
                return (
                  <div
                    key={feature.title}
                    className="verdict-card rounded-xl p-5 sm:p-6 flex flex-col gap-3"
                  >
                    <div className="w-10 h-10 rounded-lg bg-verdict-primary/10 flex items-center justify-center">
                      <FeatureIcon className="w-5 h-5 text-verdict-primary" />
                    </div>
                    <h3 className="text-sm font-semibold text-text-primary">{feature.title}</h3>
                    <p className="text-xs text-text-secondary leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>

            {/* Interactive portfolio example */}
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-5">
                <Layers className="w-4 h-4 text-verdict-primary/70" />
                <h3 className="text-sm sm:text-base font-semibold text-text-primary">See It in Action</h3>
              </div>
              <PortfolioExample />
            </div>

            {/* Email capture CTA */}
            <div className="max-w-md mx-auto">
              <p className="text-center text-text-secondary text-sm mb-3">
                Get notified when Portfolio Intelligence launches publicly.
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.target as HTMLFormElement;
                  const input = form.querySelector('input') as HTMLInputElement;
                  if (input.value.trim()) {
                    navigate(`/signup?email=${encodeURIComponent(input.value.trim())}`);
                  }
                }}
                className="flex gap-2"
              >
                <input
                  type="email"
                  placeholder="you@example.com"
                  className="flex-1 px-4 py-2.5 rounded-xl bg-surface border border-border-subtle text-text-primary placeholder-text-muted text-sm focus:outline-none focus:border-verdict-primary/50"
                />
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-verdict-primary/15 border border-verdict-primary/30 text-verdict-primary font-medium text-sm hover:bg-verdict-primary/25 transition-all"
                >
                  Notify Me
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
              <p className="text-text-muted text-xs mt-3 text-center">
                Available now in alpha for all paid plans
              </p>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section - SEO optimized */}
      {(isSmallScreen || !showCollapsedLeftPanel) && (
        <section className="relative py-20 sm:py-28" id="faq">
          <div className="verdict-divider mb-20 sm:mb-28" />
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12 sm:mb-16">
              <SectionLabel centered className="mb-4">FAQ</SectionLabel>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-5">
                Frequently Asked <span className="text-verdict-primary">Questions</span>
              </h2>
            </div>
            <div className="rounded-lg border border-border-subtle bg-surface/50 px-6">
              {LANDING_FAQ_ITEMS.map((item) => (
                <LandingFaqItem key={item.q} question={item.q} answer={item.a} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer - Hidden when Research Blueprint is full screen, always show on mobile */}
      {(isSmallScreen || !showCollapsedLeftPanel) && <VerdictFooter />}
    </div>
  );
}

